/**
 * Point-map files: one CSV / JSON schema for every field protocol, so a map
 * built once (by hand, or by retyping what a read listed) is exported and
 * reused on the next device of the same kind.
 *
 * One record per point, the same flat columns in both formats:
 *
 *   driver, name, <the driver's address fields>, type, [word_order], unit, scale, [poll_sec], [deadband]
 *
 * e.g. Modbus `driver,name,space,register,type,word_order,unit,scale`.
 *
 * - driver: modbus-tcp | modbus-rtu | opcua | s7 | enip | melsec (optional on
 *   import; the two Modbus drivers are interchangeable, they share one address).
 * - name: the metric (letters, digits, _ . -). type: a point data type
 *   (int16, uint16, int32, uint32, int64, uint64, float32, float64, bool,
 *   string; "double" reads as float64).
 * - address fields, as the driver's address (platform-core/pkg/protocol/driver.go):
 *   Modbus space, register (0-based), word_order; OPC UA node_id; S7 area, db,
 *   byte, bit; EtherNet/IP tag, elem; MELSEC device, word_order.
 * - poll_sec (whole seconds, 1 to 86400) and deadband (0 or more): optional
 *   record keys, never part of the address. An export has their columns only
 *   when a point sets one.
 *
 * JSON is `{"format": "sahas-forge.point-map", "version": 1, "driver": ..., "points": [ {...} ]}`
 * (a bare array of records also imports). CSV lines starting with # are comments.
 *
 * Import never applies anything itself: it returns every row with its record
 * or its errors, for the preview. Pure, so it is unit-tested.
 */

import Papa from "papaparse";
import { parseWordOrder, type ModbusWordOrder, type PointDataType } from "@/lib/api/things";
import { rowsToCsv, type CsvValue } from "@/lib/reports/export-csv";
import { addressFieldsOf, validateAddress } from "./address-validate";
import { DRIVER, isModbusDriver } from "./drivers";
import { HEARTBEAT_SEC_MAX, HEARTBEAT_SEC_MIN, isPollSec } from "./instrument-points";
import { POINT_DATA_TYPES, addressKey, pointAddressLabel, valueText, type PickedPoint } from "./points";
import { registersFor } from "./register-decode";
import { isBitSpace, modbusTypesFor } from "./register-map";
import { decodedText } from "./word-decode";

export const POINT_MAP_FORMAT = "sahas-forge.point-map";
export const POINT_MAP_VERSION = 1;

/** One point of a map, whatever the driver. */
export interface PointRecord {
  /** The metric name. */
  name: string;
  type: PointDataType;
  unit?: string;
  /** real = raw x scale; omitted means 1. */
  scale?: number;
  /** The point's own poll interval, whole seconds 1 to 86400; omitted means the instrument's. */
  poll_sec?: number;
  /** Publish only when the value moves more than this; omitted means none. */
  deadband?: number;
  /** The point's heartbeat, whole seconds 60 to 604800 (HEARTBEAT_SEC_MIN to HEARTBEAT_SEC_MAX): how long its last
   *  value reads as current (a result that arrives once a test); omitted means the default (a few polls). */
  max_silence_sec?: number;
  /** The driver's address object (Modbus and MELSEC carry word_order here). */
  address: Record<string, unknown>;
}

/** The columns every map has, before the driver's address fields. */
export const COMMON_COLUMNS = ["driver", "name", "type", "unit", "scale"] as const;

/** Record keys a map may carry, after scale: written only when a point sets one. */
export const OPTIONAL_COLUMNS = ["poll_sec", "deadband", "max_silence_sec"] as const;

/** A metric name the platform takes: letters, digits, _ . or - (the manual point form's rule). */
export const METRIC_PATTERN = /^[A-Za-z0-9_.-]+$/;

/** Address fields read as integers. */
const INT_FIELDS = new Set(["register", "count", "db", "byte", "bit", "elem"]);

/** A header spelling another tool may use, onto ours. */
const HEADER_ALIASES: Record<string, string> = {
  metric: "name",
  data_type: "type",
  datatype: "type",
  wordorder: "word_order",
  "word order": "word_order",
  node: "node_id",
  nodeid: "node_id",
  poll: "poll_sec",
  poll_s: "poll_sec",
  "poll (s)": "poll_sec",
  hysteresis: "deadband",
};

/** The driver's address columns in a file (count is derived from the type, so not exported). */
export function addressColumns(driver: string): string[] {
  return addressFieldsOf(driver).filter((f) => f !== "count");
}

/**
 * Every column a map for this driver has, in file order: driver, name, address,
 * type, word order, unit, scale, then poll_sec and deadband when one of the
 * given records sets them.
 */
export function mapColumns(driver: string, records: readonly PointRecord[] = []): string[] {
  const addr = addressColumns(driver);
  const place = addr.filter((f) => f !== "word_order");
  const optional = OPTIONAL_COLUMNS.filter((c) => records.some((r) => r[c] !== undefined));
  return ["driver", "name", ...place, "type", ...(addr.includes("word_order") ? ["word_order"] : []), "unit", "scale", ...optional];
}

function flatRecord(driver: string, r: PointRecord): Record<string, CsvValue> {
  const out: Record<string, CsvValue> = { driver, name: r.name, type: r.type };
  if (r.unit) out.unit = r.unit;
  if (r.scale !== undefined && r.scale !== 1) out.scale = r.scale;
  if (r.poll_sec !== undefined) out.poll_sec = r.poll_sec;
  if (r.deadband !== undefined) out.deadband = r.deadband;
  if (r.max_silence_sec !== undefined) out.max_silence_sec = r.max_silence_sec;
  for (const f of addressColumns(driver)) {
    const v = r.address[f];
    if (v !== undefined && v !== null && v !== "") out[f] = v as CsvValue;
  }
  return out;
}

/** The map as CSV (a header row, then one row per point). */
export function pointMapToCsv(driver: string, records: readonly PointRecord[]): string {
  const cols = mapColumns(driver, records);
  return rowsToCsv([cols, ...records.map((r) => cols.map((c) => flatRecord(driver, r)[c]))]);
}

/** The map as JSON. */
export function pointMapToJson(driver: string, records: readonly PointRecord[]): string {
  return JSON.stringify(
    { format: POINT_MAP_FORMAT, version: POINT_MAP_VERSION, driver, points: records.map((r) => flatRecord(driver, r)) },
    null,
    2,
  );
}

// ── import ──────────────────────────────────────────────────────────────────

/** One row of an imported file: its record when valid, else what is wrong. */
export interface ImportRow {
  /** 1-based data row (the CSV header and comments are not counted). */
  row: number;
  name: string;
  /** Where it reads, as the pickers show it ("holding 0", "DB1 byte 4", "D100"). */
  where: string;
  type: string;
  record: PointRecord | null;
  errors: string[];
}

export interface ImportResult {
  rows: ImportRow[];
  /** The file could not be read at all (not CSV / JSON, no rows). */
  error?: string;
  /** Columns in the file this driver has no use for (left out). */
  ignoredColumns: string[];
}

export interface ImportOptions {
  /** The device's driver: every row must be for it. */
  driver: string;
  /** The types the driver decodes (its registry entry); every type when unknown. */
  dataTypes?: readonly PointDataType[];
}

/** The same point address, whatever the word order (so one register or device is one point). */
export function pointIdentity(driver: string, address: Record<string, unknown>): string {
  if (isModbusDriver(driver)) {
    const space = typeof address.space === "string" && address.space ? address.space : "holding";
    return space === "holding" ? String(address.register) : `${space}:${String(address.register)}`;
  }
  const rest = { ...address };
  delete rest.word_order;
  if (typeof rest.device === "string") rest.device = rest.device.toUpperCase();
  return addressKey(rest);
}

const blank = (v: unknown) => v === undefined || v === null || (typeof v === "string" && v.trim() === "");

/** A cell as a number (a JSON number as is, a CSV cell trimmed); NaN when it is not one. */
const cellNumber = (v: unknown): number => (typeof v === "number" ? v : Number(String(v).trim()));

/** A cell as an address value: trimmed, integers for the integer fields, spellings normalised. */
function addressValue(field: string, v: unknown): unknown {
  if (typeof v === "string") v = v.trim();
  if (INT_FIELDS.has(field) && typeof v === "string" && /^-?\d+$/.test(v)) return Number(v);
  if (field === "word_order" && typeof v === "string") return parseWordOrder(v) ?? v.toLowerCase();
  if (field === "space" && typeof v === "string") return v.toLowerCase();
  if ((field === "area" || field === "device") && typeof v === "string") return v.toUpperCase();
  return v;
}

function typeOf(v: unknown): string {
  const s = typeof v === "string" ? v.trim().toLowerCase() : "";
  return s === "double" ? "float64" : s;
}

/** The types a point at this address may have. */
function allowedTypes(driver: string, address: Record<string, unknown>, dataTypes?: readonly PointDataType[]): string[] {
  if (isModbusDriver(driver)) return modbusTypesFor(typeof address.space === "string" ? address.space : "holding");
  return [...(dataTypes && dataTypes.length > 0 ? dataTypes : POINT_DATA_TYPES)];
}

function sameFamily(a: string, b: string): boolean {
  return a === b || (isModbusDriver(a) && isModbusDriver(b));
}

/** Validate one flat entry (a CSV row or a JSON object) into a record, or its errors. */
function readEntry(entry: Record<string, unknown>, row: number, opts: ImportOptions): ImportRow {
  const { driver } = opts;
  const errors: string[] = [];
  const fileDriver = typeof entry.driver === "string" ? entry.driver.trim().toLowerCase() : "";
  if (fileDriver && !sameFamily(fileDriver, driver)) {
    errors.push(`for ${fileDriver}, but this device is read over ${driver}`);
  }
  const name = typeof entry.name === "string" ? entry.name.trim() : blank(entry.name) ? "" : String(entry.name);
  if (!name) errors.push("name is required");
  else if (!METRIC_PATTERN.test(name)) errors.push("name: letters, digits, _ . or - only");

  // The driver's address fields; any other address field in the row is refused, as the strict decode does.
  const nested = entry.address && typeof entry.address === "object" ? (entry.address as Record<string, unknown>) : {};
  const flat: Record<string, unknown> = { ...nested, ...entry };
  const address: Record<string, unknown> = {};
  const own = addressFieldsOf(driver);
  for (const f of own) {
    if (!blank(flat[f])) address[f] = addressValue(f, flat[f]);
  }
  for (const f of ["space", "register", "word_order", "node_id", "area", "db", "byte", "bit", "tag", "elem", "device", "key"]) {
    if (!own.includes(f) && !blank(flat[f])) errors.push(`${f} does not apply to a ${driver} address`);
  }
  if (isModbusDriver(driver) && address.space === "holding") delete address.space;
  const addrError = validateAddress(driver, address);
  if (addrError) errors.push(addrError);

  // Type: required, except a Modbus row defaults to int16 (a bit to bool).
  let type = typeOf(entry.type);
  if (!type && isModbusDriver(driver)) type = isBitSpace(address.space as string | undefined) ? "bool" : "int16";
  const allowed = allowedTypes(driver, address, opts.dataTypes);
  if (!type) errors.push("type is required");
  else if (!allowed.includes(type)) errors.push(`type ${type} is not one of ${allowed.join(", ")}`);

  let scale: number | undefined;
  if (!blank(entry.scale)) {
    const n = cellNumber(entry.scale);
    if (!Number.isFinite(n)) errors.push("scale must be a number");
    else if (n === 0) errors.push("scale must not be 0 (leave it empty for 1)");
    else scale = n;
  }
  const unit = blank(entry.unit) ? undefined : String(entry.unit).trim();

  // Record keys, never the address: the point's own poll interval and deadband.
  let pollSec: number | undefined;
  if (!blank(entry.poll_sec)) {
    const n = cellNumber(entry.poll_sec);
    if (!isPollSec(n)) errors.push("poll_sec must be a whole number of seconds, 1 to 86400");
    else pollSec = n;
  }
  let deadband: number | undefined;
  if (!blank(entry.deadband)) {
    const n = cellNumber(entry.deadband);
    if (!Number.isFinite(n) || n < 0) errors.push("deadband must be a number, 0 or more");
    else if (n > 0) deadband = n;
  }
  let maxSilence: number | undefined;
  if (!blank(entry.max_silence_sec)) {
    const n = cellNumber(entry.max_silence_sec);
    if (!Number.isInteger(n) || n < HEARTBEAT_SEC_MIN || n > HEARTBEAT_SEC_MAX) {
      errors.push(`max_silence_sec must be a whole number of seconds, ${HEARTBEAT_SEC_MIN} to ${HEARTBEAT_SEC_MAX}`);
    } else maxSilence = n;
  }

  const record: PointRecord | null =
    errors.length === 0
      ? {
          name,
          type: type as PointDataType,
          address,
          ...(unit ? { unit } : {}),
          ...(scale !== undefined && scale !== 1 ? { scale } : {}),
          ...(pollSec !== undefined ? { poll_sec: pollSec } : {}),
          ...(deadband !== undefined ? { deadband } : {}),
          ...(maxSilence !== undefined ? { max_silence_sec: maxSilence } : {}),
        }
      : null;
  return { row, name, where: Object.keys(address).length > 0 ? pointAddressLabel(address) : "no address", type, record, errors };
}

/** Header cells as our column names (lower case, aliases folded). */
function normaliseHeader(h: string): string {
  const k = h.replace(/^\uFEFF/, "").trim().toLowerCase();
  return HEADER_ALIASES[k] ?? k;
}

function csvEntries(text: string): { entries: Record<string, unknown>[]; columns: string[]; error?: string } {
  const parsed = Papa.parse<Record<string, string>>(text.replace(/^\uFEFF/, ""), {
    header: true,
    skipEmptyLines: "greedy",
    comments: "#",
    transformHeader: normaliseHeader,
  });
  const columns = (parsed.meta.fields ?? []).filter(Boolean);
  if (!columns.includes("name")) {
    return { entries: [], columns, error: 'Not a point map: the first row must be the column names, with a "name" column.' };
  }
  return { entries: parsed.data, columns };
}

function jsonEntries(text: string): { entries: Record<string, unknown>[]; columns: string[]; error?: string } {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return { entries: [], columns: [], error: "Could not read that file as JSON." };
  }
  const list = Array.isArray(value)
    ? value
    : value && typeof value === "object" && Array.isArray((value as { points?: unknown }).points)
      ? (value as { points: unknown[] }).points
      : null;
  if (!list) return { entries: [], columns: [], error: 'Not a point map: expected {"points": [...]} or a list of points.' };
  const fileDriver = value && typeof value === "object" && !Array.isArray(value) ? (value as { driver?: unknown }).driver : undefined;
  const entries = list.map((e) => {
    const o: Record<string, unknown> = {};
    if (e && typeof e === "object" && !Array.isArray(e)) {
      for (const [k, v] of Object.entries(e as Record<string, unknown>)) o[normaliseHeader(k)] = v;
    }
    if (o.driver === undefined && typeof fileDriver === "string") o.driver = fileDriver;
    return o;
  });
  const columns = [...new Set(entries.flatMap((e) => Object.keys(e)))];
  return { entries, columns };
}

/**
 * Read a point-map file for a device: every row with its record or its errors.
 * The format is JSON when the text starts with { or [, else CSV (or as given).
 * A repeated name or address is an error on the later row.
 */
export function parsePointMap(text: string, opts: ImportOptions, format?: "csv" | "json"): ImportResult {
  const trimmed = text.replace(/^\uFEFF/, "").trimStart();
  const fmt = format ?? (trimmed.startsWith("{") || trimmed.startsWith("[") ? "json" : "csv");
  const { entries, columns, error } = fmt === "json" ? jsonEntries(trimmed) : csvEntries(trimmed);
  if (error) return { rows: [], error, ignoredColumns: [] };
  if (entries.length === 0) return { rows: [], error: "The file has no points.", ignoredColumns: [] };

  const known = new Set<string>([...COMMON_COLUMNS, ...OPTIONAL_COLUMNS, ...addressFieldsOf(opts.driver), "address"]);
  const other = new Set(["space", "register", "word_order", "node_id", "area", "db", "byte", "bit", "tag", "elem", "device", "key"]);
  const ignoredColumns = columns.filter((c) => !known.has(c) && !other.has(c));

  const names = new Map<string, number>();
  const places = new Map<string, number>();
  const rows = entries.map((entry, i) => {
    const r = readEntry(entry, i + 1, opts);
    if (r.record) {
      const prevName = names.get(r.record.name);
      const place = pointIdentity(opts.driver, r.record.address);
      const prevPlace = places.get(place);
      if (prevName !== undefined) r.errors.push(`name repeats row ${prevName}`);
      if (prevPlace !== undefined) r.errors.push(`address repeats row ${prevPlace}`);
      if (r.errors.length > 0) r.record = null;
      else {
        names.set(r.name, r.row);
        places.set(place, r.row);
      }
    }
    return r;
  });
  return { rows, ignoredColumns };
}

// ── picked points (non-Modbus drivers) ──────────────────────────────────────

/** A picked point as a map record. */
export function pointToRecord(p: PickedPoint): PointRecord {
  return {
    name: p.metric,
    type: p.data_type,
    address: p.address,
    ...(p.unit ? { unit: p.unit } : {}),
    ...(p.scale !== undefined && p.scale !== 1 ? { scale: p.scale } : {}),
    ...(p.poll_sec !== undefined ? { poll_sec: p.poll_sec } : {}),
    ...(p.deadband !== undefined ? { deadband: p.deadband } : {}),
    ...(p.max_silence_sec !== undefined ? { max_silence_sec: p.max_silence_sec } : {}),
  };
}

/** A map record as a picked point. */
export function recordToPoint(r: PointRecord): PickedPoint {
  return {
    key: addressKey(r.address),
    metric: r.name,
    address: r.address,
    data_type: r.type,
    label: pointAddressLabel(r.address),
    ...(r.unit ? { unit: r.unit } : {}),
    ...(r.scale !== undefined ? { scale: r.scale } : {}),
    ...(r.poll_sec !== undefined ? { poll_sec: r.poll_sec } : {}),
    ...(r.deadband !== undefined ? { deadband: r.deadband } : {}),
    ...(r.max_silence_sec !== undefined ? { max_silence_sec: r.max_silence_sec } : {}),
  };
}

/**
 * Apply imported records to the picked points: replace them all, or append,
 * where a record at a point already picked updates that point (its name, type,
 * unit, scale, poll interval, deadband and address) instead of adding a second one.
 */
export function mergePoints(
  driver: string,
  picked: readonly PickedPoint[],
  records: readonly PointRecord[],
  mode: "append" | "replace",
): { points: PickedPoint[]; added: number; updated: number } {
  if (mode === "replace") return { points: records.map(recordToPoint), added: records.length, updated: 0 };
  const out = [...picked];
  let added = 0;
  let updated = 0;
  for (const r of records) {
    const id = pointIdentity(driver, r.address);
    const i = out.findIndex((p) => pointIdentity(driver, p.address) === id);
    if (i >= 0) {
      const fresh = recordToPoint(r);
      out[i] = {
        ...out[i],
        metric: fresh.metric,
        data_type: fresh.data_type,
        address: fresh.address,
        unit: fresh.unit,
        scale: fresh.scale,
        poll_sec: fresh.poll_sec,
        deadband: fresh.deadband,
        max_silence_sec: fresh.max_silence_sec,
      };
      updated++;
    } else {
      out.push(recordToPoint(r));
      added++;
    }
  }
  return { points: out, added, updated };
}

/** The driver takes a word order for this type: Modbus and MELSEC, a value of 2+ words. */
export function wordOrderApplies(driver: string, type: string): boolean {
  return addressFieldsOf(driver).includes("word_order") && registersFor(type) >= 2;
}

/** The word order a point reads with: its own, else the driver's default (MELSEC low word first). */
export function pointWordOrder(driver: string, address: Record<string, unknown>): ModbusWordOrder {
  const wo = typeof address.word_order === "string" ? parseWordOrder(address.word_order) : undefined;
  return wo ?? (driver === DRIVER.MELSEC ? "little" : "big");
}

/** One picked point's editable fields; `scale: null` clears the scale. */
export interface PointEdit {
  name?: string;
  type?: PointDataType;
  unit?: string;
  scale?: number | null;
  word_order?: ModbusWordOrder;
}

/**
 * Edit the picked point at `key` (its address stays; add a point for another
 * one). A new type drops the value read as the old one; a word order is kept
 * on the address, where the driver reads it.
 */
export function editPoint(points: readonly PickedPoint[], key: string, edit: PointEdit): PickedPoint[] {
  return points.map((p) => {
    if (p.key !== key) return p;
    const out: PickedPoint = { ...p };
    if (edit.name !== undefined) out.metric = edit.name;
    if (edit.type && edit.type !== p.data_type) {
      out.data_type = edit.type;
      delete out.value;
    }
    if (edit.unit !== undefined) {
      if (edit.unit) out.unit = edit.unit;
      else delete out.unit;
    }
    if (edit.scale !== undefined) {
      if (edit.scale === null || edit.scale === 1) delete out.scale;
      else out.scale = edit.scale;
    }
    if (edit.word_order !== undefined) out.address = { ...p.address, word_order: edit.word_order };
    return out;
  });
}

/** A picked point's sampled value (the browse's reading), scaled when it has a scale. */
export function pointSampled(p: PickedPoint): { text: string; raw?: string } {
  const v = p.value;
  if (typeof v === "number") {
    const scaled = p.scale !== undefined && p.scale !== 1;
    return scaled ? { text: decodedText(v * p.scale!), raw: decodedText(v) } : { text: decodedText(v) };
  }
  return { text: valueText(v) };
}

/** Name problems among the picked points: an invalid metric, or a name used twice. Key -> problem. */
export function pointNameProblems(points: readonly PickedPoint[]): Map<string, string> {
  const out = new Map<string, string>();
  const first = new Map<string, PickedPoint>();
  for (const p of points) {
    const name = p.metric.trim();
    if (!name) out.set(p.key, "Name this point.");
    else if (!METRIC_PATTERN.test(name)) out.set(p.key, "Letters, digits, _ . or - only.");
    else if (first.has(name)) out.set(p.key, `Same name as ${first.get(name)!.label}.`);
    else first.set(name, p);
  }
  return out;
}
